/**
 * useBrokerAuth.ts — broker login/logout/refresh orchestration (Story ADMIN-W4).
 *
 * Plain module functions (no hook state needed — the session store is the state):
 *   startLogin(redirectPath)  — begin the code+PKCE redirect to Keycloak
 *   handleCallback(code, state) — validate state, exchange, store the session
 *   logout()                  — clear the session + Keycloak end-session redirect
 *   ensureFreshToken()        — single-flight refresh when < 60 s to expiry
 *   hasBrokerSession()        — the route-gate predicate
 *
 * First login walks through Keycloak's CONFIGURE_TOTP required action (QR +
 * code, Keycloak-hosted) inside the authorize redirect — no custom 2FA UI (AC 7).
 *
 * [Source: docs/stories/admin-w4-broker-auth.md Tasks 6/7]
 */

import {
  buildAuthorizationUrl,
  buildEndSessionUrl,
  consumeFlowState,
  exchangeCode,
  refreshTokens,
} from '../../lib/auth/oidc';
import {
  clearBrokerSession,
  loadBrokerSession,
  sessionFromTokenSet,
  storeBrokerSession,
} from '../../lib/auth/auth-session';

/** Refresh proactively when the access token has < this many ms left. */
export const REFRESH_HEADROOM_MS = 60_000;

/** True when a broker session snapshot exists (expired-but-refreshable counts). */
export function hasBrokerSession(): boolean {
  return loadBrokerSession() !== null;
}

/**
 * Begin the Authorization-Code+PKCE redirect (Keycloak hosts credentials + TOTP).
 * `idpHint` (WAVE2-SSO) jumps straight to that identity provider (e.g. Microsoft),
 * skipping Keycloak's own login page — used by the passwordless SSO activation.
 */
export async function startLogin(redirectPath: string = '/', idpHint?: string): Promise<void> {
  const url = await buildAuthorizationUrl(redirectPath, idpHint);
  window.location.assign(url);
}

/**
 * The post-login destination must be an IN-APP path: a plain '/…' (not '//…',
 * which the URL parser treats as scheme-relative). Anything else — an absolute
 * URL smuggled in via /login?redirect=… or a tampered flow state — falls back
 * to '/' (open-redirect hardening; review fix).
 */
export function safeRedirectPath(path: string | undefined): string {
  return path && path.startsWith('/') && !path.startsWith('//') ? path : '/';
}

/**
 * Handle the /auth/callback query: validate the round-trip state, exchange the
 * code, store the session. Returns the originally requested IN-APP path
 * (sanitized — see safeRedirectPath).
 * Throws on state mismatch / missing flow state / exchange failure (the page
 * shows a uniform error + a link back to /login — never token material).
 */
export async function handleCallback(code: string, state: string): Promise<string> {
  const flow = consumeFlowState();
  if (!flow || flow.state !== state) {
    throw new Error('OIDC state mismatch');
  }
  const tokens = await exchangeCode(code, flow.verifier);
  storeBrokerSession(sessionFromTokenSet(tokens));
  return safeRedirectPath(flow.redirectPath);
}

/** Clear the local session and end the Keycloak SSO session (→ back to /login). */
export function logout(): void {
  const session = loadBrokerSession();
  clearBrokerSession();
  window.location.assign(buildEndSessionUrl(session?.idToken ?? ''));
}

// ── single-flight refresh ─────────────────────────────────────────────────────

let refreshInFlight: Promise<string | null> | null = null;

/**
 * FORCE one refresh grant (single-flight — concurrent callers await the same
 * promise). Used by ensureFreshToken near expiry AND by the api-client's
 * 401-retry (a server-side revocation can 401 a fresh-looking token). Returns
 * null without a session; a FAILED refresh clears the session (the caller
 * redirects to /login).
 */
export async function refreshAccessToken(): Promise<string | null> {
  const session = loadBrokerSession();
  if (!session) return null;
  refreshInFlight ??= (async () => {
    try {
      const tokens = await refreshTokens(session.refreshToken);
      // Keycloak rotates refresh tokens — always store the NEWEST set.
      storeBrokerSession(sessionFromTokenSet(tokens));
      return tokens.accessToken;
    } catch {
      clearBrokerSession();
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

/**
 * Return a live access token: the stored one while fresh, otherwise the shared
 * forced refresh. Returns null without a session.
 */
export async function ensureFreshToken(): Promise<string | null> {
  const session = loadBrokerSession();
  if (!session) return null;
  if (Date.now() < session.expiresAt - REFRESH_HEADROOM_MS) {
    return session.accessToken;
  }
  return refreshAccessToken();
}
